import axios from "axios";
import moment from "moment";

import { apiConfig } from "../config/authConfig";
import { CredentialManager } from "../classes/credentialManager";
import { IWasteCharacterization, WasteCharacterizationEmailDocumentRequest, WasteCharacterizationUpdateSignedRequest } from "../types/wasteCharacterization";


export namespace WasteCharacterizationService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one WasteCharacterization.
	 * @param data WasteCharacterization response data from an API.
	 **********/
	export function mapWasteCharacterization(data: any) {
		let wasteCharacterization: IWasteCharacterization = {
			id: data.id,
			customerId: data.customerId,
			customerName: data.customerName,
			materialId: data.materialId,
			materialName: data.materialName,
			constituents: data.constituents,
			dotWasteStream: data.dotWasteStream,
			unNumber: data.unNumber,
			generatingProcess: data.generatingProcess,
			physicalState: data.physicalState,
			byProcess: data.byProcess,
			processDocFormFile: undefined,
			processDocContent: data.processDocContent,
			byTesting: data.byTesting,
			testingDocFormFile: undefined,
			testingDocContent: data.testingDocContent,
			includeProfileDocument: data.includeProfileDocument,
			profileDocumentFormFile: undefined,
			profileDocumentContent: data.profileDocumentContent,
			isSolid: data.isSolid,
			isSolidExclusion: data.isSolidExclusion,
			isIgnitable: data.isIgnitable,
			flashPoint: data.flashPoint,
			isCorrosive: data.isCorrosive,
			ph: data.ph,
			isReactive: data.isReactive,
			isToxic: data.isToxic,
			toxicityCodes: data.toxicityCodes,
			rcraWasteDetermination: data.rcraWasteDetermination,
			isLdrApplicable: data.isLdrApplicable,
			isWastewaterTreatable: data.isWastewaterTreatable,
			mainDoc: data.mainDoc,
			createdBy: data.createdBy,
			createdByTitle: data.createdByTitle,
			createdByEmail: data.createdByEmail,
			environmentalManagerName: data.environmentalManagerName,
			environmentalManagerTitle: data.environmentalManagerTitle,
			environmentalManagerEmail: data.environmentalManagerEmail,
			createdOn: data.createdOn ? moment(data.createdOn).toDate() : undefined,
			expiresOn: data.expiresOn ? moment(data.expiresOn).toDate() : undefined,
			emailedOn: data.emailedOn ? moment(data.emailedOn).toDate() : undefined,
			completedOn: data.completedOn ? moment(data.completedOn).toDate() : undefined,
			status: data.status,
			profileNumber: data.profileNumber,
			emailedTo: data.emailedTo
		}

		return wasteCharacterization;
	}

	/**********
	 * Convert an array of api response objects to an array of WasteCharacterizations.
	 * @param data WasteCharacterization data from an API response.
	 **********/
	export function mapWasteCharacterizations(data: any) {
		let items: IWasteCharacterization[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				items.push(mapWasteCharacterization(item));
			})
		}

		return items;
	}

	/**********
	 * Gets all waste characterizations.
	 **********/
	export async function getWasteCharacterizations() {
		const apiResponse = axios.get(
			apiConfig.default + "/wastecharacterization/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Get a single waste characterization.
	 * @param id The waste characterization id.
	 **********/
	export async function getWasteCharacterization(id: string): Promise<IWasteCharacterization> {
		const metaResponse = await axios.get(
			apiConfig.default + "/wastecharacterization/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
				},
			}
		);

		return metaResponse.data;
	}

	/**********
	 * Add a new waste characterization.
	 * @param wasteCharacterization The waste characterization information.
	 **********/
	export async function addWasteCharacterization(wasteCharacterization: IWasteCharacterization) {
		const apiResponse = axios.put(
			apiConfig.default + "/wastecharacterization",
			wasteCharacterization,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'multipart/form-data',
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Delete a waste characterization.
	 * @param id The waste characterization ID.
	 **********/
	export async function deleteWasteCharacterization(id: string) {
		const apiResponse = axios.delete(
			apiConfig.default + "/wastecharacterization/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	};

	/**********
	 * Emails a Waste Characterization document to be signed.
	 * @param request The email request containing the recipient, subject, and the Waste Characterization record ID.
	 **********/
	export async function emailDocument(request: WasteCharacterizationEmailDocumentRequest) {
		const apiResponse = axios.post(
			apiConfig.default + `/wastecharacterization/document/email`,
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Uploads a main document PDF that has been approved and signed.
	 * @param request The upload request containing the new document contents and the Waste Characterization record ID.
	 **********/
	export async function uploadSignedDocument(request: WasteCharacterizationUpdateSignedRequest) {
		const apiResponse = axios.post(
			apiConfig.default + `/wastecharacterization/document/signed`,
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'multipart/form-data',
				},
			}
		)

		return apiResponse;
	}
	
	/**********
	 * Gets the main document PDF generated by the Waste Characterization service.
	 * @param wasteCharacterizationId The id of the waste characterization that owns the document.
	 **********/
	export async function getMainDocument(wasteCharacterizationId: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/wastecharacterization/${wasteCharacterizationId}/document/main`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}
}
